import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import "./MyTechProfileThirdParty.css";
import { useSelector } from "react-redux";

// --- Reusable Pie Chart Component (No changes) ---
const CustomPieChart = ({ data }) => {
  const COLORS = ["#4CAF50", "#FFC107", "#F44336"];
  const isDataEmpty = !data || data.every((item) => item.value === 0);

  if (isDataEmpty) {
    return (
      <div className="mytechprofilethirdparty__placeholder">
        No data available to display the chart.
      </div>
    );
  }

  const totalSolved = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="mytechprofilethirdparty__chart-wrapper">
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={100}
            innerRadius={85}
            fill="#8884d8"
            dataKey="value"
            nameKey="name"
            isAnimationActive={true}
            animationDuration={800}
            paddingAngle={2}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                stroke={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [`${value} problems`, name]}
            contentStyle={{
              backgroundColor: "rgba(30, 30, 45, 0.9)",
              borderColor: "rgba(76, 175, 80, 0.1)",
              borderRadius: "8px",
            }}
            cursor={{ fill: "rgba(76, 175, 80, 0.1)" }}
            labelStyle={{ color: "#fff" }}
            itemStyle={{ color: "#fff", fontFamily: "Questrial" }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="mytechprofilethirdparty__center-text">
        <div className="mytechprofilethirdparty__total-solved-value">
          {totalSolved}
        </div>
        <div className="mytechprofilethirdparty__total-solved-label">
          Problems Solved
        </div>
      </div>
    </div>
  );
};

// --- Detailed Stats Display Component (No changes) ---
const DetailedStats = ({ data }) => {
  if (!data || data.length < 3) return null;

  const legendData = [
    { name: "Easy", color: "#4CAF50", value: data[0].value },
    { name: "Medium", color: "#FFC107", value: data[1].value },
    { name: "Hard", color: "#F44336", value: data[2].value },
  ];

  return (
    <div className="mytechprofilethirdparty__detailed-stats">
      {legendData.map((item, index) => (
        <div key={index} className="mytechprofilethirdparty__detailed-item">
          <span
            className="mytechprofilethirdparty__detailed-color-box"
            style={{ backgroundColor: item.color }}
          ></span>
          <span className="mytechprofilethirdparty__detailed-label">
            {item.name}
          </span>
          <span className="mytechprofilethirdparty__detailed-value">
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// --- Platform Statistics Component (UPDATED with new layout) ---
const PlatformStats = ({ platformData }) => {
  const { platformName, username, stats, badges, id } = platformData;

  const platformLogos = {
    leetcode: "/images/leetcode.png", // Ensure this path is correct
    gfg: "/images/gfg.jpg", // Ensure this path is correct
  };

  // Select the logo based on the platform 'id'
  const logoUrl =
    platformLogos[id.toLowerCase()] ||
    "https://api.dicebear.com/6.x/pixel-art/svg?seed=placeholder";

  const pieData = stats
    ? [
        { name: "Easy", value: stats.easy || 0 },
        { name: "Medium", value: stats.medium || 0 },
        { name: "Hard", value: stats.hard || 0 },
      ]
    : [];

  return (
    <div className="mytechprofilethirdparty__section">
      <div className="mytechprofilethirdparty__user-header">
        {/* Avatar Image */}
        <img
          src={logoUrl}
          alt={`${username}'s avatar`}
          className="mytechprofilethirdparty__avatar"
        />

        {/* Wrapper for username and profile button */}
        <div className="mytechprofilethirdparty__user-info-wrapper">
          <span className="mytechprofilethirdparty__username">{username}</span>
          <a
            href={
              id === "leetcode"
                ? `https://leetcode.com/u/${username}`
                : `https://www.geeksforgeeks.org/user/${username}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="mytechprofilethirdparty__button"
          >
            Profile
          </a>
        </div>
      </div>

      <div className="mytechprofilethirdparty__stats-container">
        <div className="mytechprofilethirdparty__chart-section">
          <h4 className="mytechprofilethirdparty__component-title">
            Problem Distribution
          </h4>
          <CustomPieChart data={pieData} />
          <DetailedStats data={pieData} />
        </div>

        {platformName === "LeetCode" && badges && badges.length > 0 && (
          <div className="mytechprofilethirdparty__badges-section">
            <h4 className="mytechprofilethirdparty__component-title">
              Earned Badges
            </h4>
            <div
              className="mytechprofilethirdparty__badges-container"
              data-count={badges?.length}
            >
              {badges?.map(
                (badge) =>
                  badge?.url?.length > 0 && (
                    <img
                      key={badge.id}
                      src={badge.url}
                      alt={badge.alt}
                      className="mytechprofilethirdparty__badge-image"
                    />
                  )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MyTechProfileThirdParty = () => {
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  useEffect(() => {
    if (profile && profile.technicalAchievements) {
      const { currentActiveTab, platforms = [] } =
        profile.technicalAchievements;

      const foundPlatform = platforms.find(
        (platform) =>
          platform.id.toLowerCase() === currentActiveTab?.toLowerCase()
      );

      setSelectedPlatform(foundPlatform || null);
    }
  }, [profile]);

  if (!selectedPlatform) {
    return (
      <div className="mytechprofilethirdparty__placeholder">
        Please select a LeetCode or GFG profile to view stats.
      </div>
    );
  }

  return (
    <div className="mytechprofilethirdparty__container">
      <PlatformStats platformData={selectedPlatform} />
    </div>
  );
};

export default MyTechProfileThirdParty;
